import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import stateList from '../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PurchaseContract = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    id: '',
    purchase_contract_no: '',
    contract_date: '',
    state_of_purchase: '',
    state_code: '',
    commodity_id: '',
    commodity_full: '',
    supplier_id: '',
    broker_id: '',
    broker_rate: '',
    cma_id: '',
    warehouse_id: '',
    total_qty_mt: '',
    unit_price_inr: '',
    unit_cost_inr: '',
    total_landed_cost_inr: '',
    vessel_name: '',
    quantity_tolerance_pct: '',
    markup_pct: '',

    expected_shipment_start: '',
    expected_shipment_end: '',
    remarks: '',
    status: 'Open',
    contract_maker: 'By Us',
    port_name: '',
    payment_term: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [commodities, setCommodities] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const navigate = useNavigate();

  // Payment Term options
  const paymentTermOptions = [
    { value: '30 Days', name: '30 Days' },
    { value: '45 Days', name: '45 Days' },
    { value: '60 Days', name: '60 Days' },
    { value: '90 Days', name: '90 Days' },
    { value: 'Against Delivery', name: 'Against Delivery' },
    { value: 'Advance', name: 'Advance' },
  ];

  const contractMakerOptions = [
    { value: 'By Us', name: 'By Us' },
    { value: 'By Broker', name: 'By Broker' },
  ];

  const statusOptions = [
    { value: 'Open', name: 'Open' },
    { value: 'Closed', name: 'Closed' },
    { value: 'Cancelled', name: 'Cancelled' },
  ];



  const FIELD_LIMITS = {
    purchase_contract_no: 60,
    contract_date: 10,
    state_of_purchase: 60,
    state_code: 45,
    commodity_full: 101,
    vessel_name: 120,
    port_name: 100,
    payment_term: 60,
    expected_shipment_start: 10,
    remarks: 1000,
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace('_', ' ')}`;
    }
    // Required fields
    const requiredFields = [
      'purchase_contract_no', 'contract_date', 'state_of_purchase', 'state_code',
      'commodity_id', 'supplier_id', 'broker_id', 'cma_id',
      'total_qty_mt', 'unit_price_inr', 'quantity_tolerance_pct',
      'expected_shipment_start', 'expected_shipment_end'
    ];
    if (requiredFields.includes(name) && (!value || value === '' || value === 0)) {
      error = `${name.replace('_', ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  const fetchContractData = useCallback(async () => {
    if (!isEditMode) return;
  
    setLoadingData(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/contracts/purchase-contract-get/${id}`, {
        credentials: 'include'
      });
  
      if (res.ok) {
        const data = await res.json();
        const contract = data.data;
  
        setFormData({
          id: contract.id || '',
          purchase_contract_no: contract.purchase_contract_no || '',
          contract_date: contract.contract_date || '',
          state_of_purchase: contract.state_of_purchase || '',
          state_code: contract.state_code || '',
          commodity_id: contract.commodity_id || '',
          commodity_full: contract.commodity_full || '',
          supplier_id: contract.supplier_id || '',
          broker_id: contract.broker_id || '',
          broker_rate: contract.broker_rate || '',
          cma_id: contract.cma_id || '',
          warehouse_id: contract.warehouse_id || '',
          total_qty_mt: contract.total_qty_mt || '',
          unit_price_inr: contract.unit_price_inr || '',
          unit_cost_inr: contract.unit_cost_inr || '',
          total_landed_cost_inr: contract.total_landed_cost_inr || '',
          vessel_name: contract.vessel_name || '',
          quantity_tolerance_pct: contract.quantity_tolerance_pct || '',
          markup_pct: contract.markup_pct || '',

          expected_shipment_start: contract.expected_shipment_start ? contract.expected_shipment_start.split('T')[0] : '',
          expected_shipment_end: contract.expected_shipment_end ? contract.expected_shipment_end.split('T')[0] : '',
          remarks: contract.remarks || '',
          status: contract.status || 'Open',
          contract_maker: contract.contract_maker || 'By Us',
          port_name: contract.port_name || '',
          payment_term: contract.payment_term || ''
        });
      } else {
        const errorText = await res.text();
        console.error('Fetch contract failed:', errorText);
        toast.error('Failed to fetch contract data');
      }
    } catch (error) {
      console.error('Error fetching contract data:', error);
      toast.error('Error loading contract data');
    } finally {
      setLoadingData(false);
    }
  }, [isEditMode, id]);
  
  // Fetch dropdown data using common masters-get API
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        console.log('Fetching dropdown data...');
        
        // Fetch commodities
        const commodityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
          credentials: 'include'
        });
        if (commodityRes.ok) {
          const commodityData = await commodityRes.json();
          setCommodities(commodityData.data || []);
        } else {
          console.error('Failed to fetch commodities:', commodityRes.status);
        }

        // Fetch suppliers
        const supplierRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-suppliers`, {
          credentials: 'include'
        });
        if (supplierRes.ok) {
          const supplierData = await supplierRes.json();
          console.log('Suppliers:', supplierData.data);
          setSuppliers(supplierData.data || []);
        } else {
          console.error('Failed to fetch suppliers:', supplierRes.status);
        }

        // Fetch brokers
        const brokerRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-brokers`, {
          credentials: 'include'
        });
        if (brokerRes.ok) {
          const brokerData = await brokerRes.json();
          console.log('Brokers:', brokerData.data);
          setBrokers(brokerData.data || []);
        } else {
          console.error('Failed to fetch brokers:', brokerRes.status);
        }

        // CHAs fetch removed
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };

    fetchDropdownData();
  }, []);

  // Fetch contract data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchContractData();
    }
  }, [isEditMode, fetchContractData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => {
      const updated = { ...prev, [fieldName]: value };
      // Clear broker_id if contract_maker is 'By Us'
      if (fieldName === 'contract_maker' && value === 'By Us') {
        updated.broker_id = '';
      }
      return updated;
    });
  };

  const handleStateChange = (stateName) => {
    const selectedState = stateList.find(state => state.name === stateName);
    setFormData(prev => ({ 
      ...prev, 
      state_of_purchase: stateName,
      state_code: selectedState ? selectedState.code : ''
    }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validation: check all fields for max length
    for (const key in formData) {
      if (FIELD_LIMITS[key] && formData[key] && formData[key].length > FIELD_LIMITS[key]) {
        toast.error(`Maximum ${FIELD_LIMITS[key]} characters allowed for ${key.replace('_', ' ')}`);
        setLoading(false);
        return;
      }
    }
    
    // Validation: check required fields
    const requiredFields = [
      'purchase_contract_no', 'contract_date', 'state_of_purchase', 'state_code',
      'commodity_id', 'supplier_id',
      'total_qty_mt', 'unit_price_inr', 'quantity_tolerance_pct',
      'expected_shipment_start', 'expected_shipment_end'
    ];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field] === '' || formData[field] === 0) {
        toast.error(`${field.replace('_', ' ')} is required`);
        setLoading(false);
        return;
      }
    }
    
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/contracts/purchase-contract-put`
        : `${API_BASE_URL}/api/contracts/purchase-contract-post`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        const message = isEditMode ? 'Purchase Contract Updated Successfully!' : 'Purchase Contract Created Successfully!';
        toast.success(message);
        
        if (!isEditMode) {
          // Reset form only for new contracts
          setFormData({
            id: '',
            purchase_contract_no: '',
            contract_date: '',
            state_of_purchase: '',
            state_code: '',
            commodity_id: '',
            commodity_full: '',
            supplier_id: '',
            broker_id: '',
            broker_rate: '',
            total_qty_mt: '',
            unit_price_inr: '',
            unit_cost_inr: '',
            total_landed_cost_inr: '',
            vessel_name: '',
            quantity_tolerance_pct: '',
            markup_pct: '',
            expected_shipment_start: '',
            expected_shipment_end: '',
            remarks: '',
            status: 'Open',
            contract_maker: 'By Us',
            port_name: '',
            payment_term: ''
          });
        }
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
        <h2 className="text-[15px] font-bold mb-0 text-left">
          {isEditMode ? 'Edit Purchase Contract' : 'Purchase Contract Entry'}
        </h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      {loadingData && isEditMode ? (
        <div className="text-center text-blue-600 py-6 text-[13px]">Loading contract data...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-0">
          <div className="px-3 pt-4 pb-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-medium mb-1">Contract No <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="purchase_contract_no" 
                  value={formData.purchase_contract_no} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  maxLength={FIELD_LIMITS.purchase_contract_no}
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                  required 
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Contract Date <span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  name="contract_date" 
                  value={formData.contract_date} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Status <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={statusOptions}
                  value={formData.status}
                  onChange={(value) => handleDropdownChange('status', value)}
                  placeholder="Select Status"
                  displayKey="name"
                  valueKey="value"
                  required
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Contract Maker <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={contractMakerOptions}
                  value={formData.contract_maker}
                  onChange={(value) => handleDropdownChange('contract_maker', value)}
                  placeholder="Select Contract Maker"
                  displayKey="name"
                  valueKey="value"
                  required
                />
              </div>

              {formData.contract_maker === 'By Broker' && (
              <>
                <div>
                  <label className="block text-[12px] font-medium mb-1">Broker</label>
                  <SearchableDropdown
                    options={brokers}
                    value={formData.broker_id}
                    onChange={(value) => handleDropdownChange('broker_id', value)}
                    placeholder="Select Broker"
                    displayKey="name"
                    valueKey="broker_id"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium mb-1">Broker Rate (Rs/MT)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    name="broker_rate" 
                    value={formData.broker_rate} 
                    onChange={handleChange} 
                    className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                  />
                </div>
              </>
              )}

              <div>
                <label className="block text-[12px] font-medium mb-1">State of Purchase <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={stateList}
                  value={formData.state_of_purchase}
                  onChange={handleStateChange}
                  placeholder="Select State"
                  displayKey="name"
                  valueKey="name"
                  required
                  onBlur={handleBlur}
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Port Name</label>
                <input 
                  type="text" 
                  name="port_name" 
                  value={formData.port_name} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  maxLength={FIELD_LIMITS.port_name}
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Commodity <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={commodities}
                  value={formData.commodity_id}
                  onChange={(value) => handleDropdownChange('commodity_id', value)}
                  placeholder="Select Commodity"
                  displayKey="name"
                  valueKey="commodity_id"
                  required
                  customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
                />
              </div>

              

              <div>
                <label className="block text-[12px] font-medium mb-1">Supplier <span className="text-red-500">*</span></label>
                <SearchableDropdown
                  options={suppliers}
                  value={formData.supplier_id}
                  onChange={(value) => handleDropdownChange('supplier_id', value)}
                  placeholder="Select Supplier"
                  displayKey="name"
                  valueKey="supplier_id"
                  required
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Total Qty (MT) <span className="text-red-500">*</span></label>
                <input 
                  type="number" 
                  step="0.001"
                  name="total_qty_mt" 
                  value={formData.total_qty_mt} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Unit Price (INR) <span className="text-red-500">*</span></label>
                <input 
                  type="number" 
                  step="0.01"
                  name="unit_price_inr" 
                  value={formData.unit_price_inr} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Unit Cost (INR)</label>
                <input 
                  type="number" 
                  step="0.01"
                  name="unit_cost_inr" 
                  value={formData.unit_cost_inr} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Total Landed Cost (INR)</label>
                <input 
                  type="number" 
                  step="0.01"
                  name="total_landed_cost_inr" 
                  value={formData.total_landed_cost_inr} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Payment Term</label>
                <SearchableDropdown
                  options={paymentTermOptions}
                  value={formData.payment_term}
                  onChange={(value) => handleDropdownChange('payment_term', value)}
                  placeholder="Select Payment Term"
                  displayKey="name"
                  valueKey="value"
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Vessel Name</label>
                <input 
                  type="text" 
                  name="vessel_name" 
                  value={formData.vessel_name} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  maxLength={FIELD_LIMITS.vessel_name}
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                />
              </div>



              <div>
                <label className="block text-[12px] font-medium mb-1">Quantity Tolerance (%) <span className="text-red-500">*</span></label>
                <input 
                  type="number" 
                  step="0.01"
                  name="quantity_tolerance_pct" 
                  value={formData.quantity_tolerance_pct} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Markup (%)</label>
                <input 
                  type="number" 
                  step="0.01"
                  name="markup_pct" 
                  value={formData.markup_pct} 
                  onChange={handleChange} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                />
              </div>



              <div>
                <label className="block text-[12px] font-medium mb-1">Expected Shipment Start Date <span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  name="expected_shipment_start" 
                  value={formData.expected_shipment_start} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                  required 
                />
              </div>

              <div>
                <label className="block text-[12px] font-medium mb-1">Expected Shipment End Date <span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  name="expected_shipment_end" 
                  value={formData.expected_shipment_end} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none" 
                  required 
                />
              </div>            </div>

            <div className="mt-3">
              <label className="block text-[12px] font-medium mb-1">Remarks</label>
              <textarea 
                name="remarks" 
                value={formData.remarks} 
                onChange={handleChange} 
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.remarks}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none resize-none" 
                rows={3}
              ></textarea>
            </div>
          </div>

          <div className="px-3 pt-2 pb-4">
            <button
              type="submit"
              className="w-auto px-3 py-1.5 text-[13px] bg-[#15803d] text-white font-semibold tracking-wide rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60"
              disabled={loading || loadingData}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Purchase Contract' : 'Save Purchase Contract')}
            </button>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
          </div>
        </form>
      )}
    </div>
  );
};

export default PurchaseContract;
